import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { format, isToday, isYesterday, differenceInSeconds } from "date-fns";
import { ru } from "date-fns/locale";
import { CURRENCY_SYMBOL, COMMISSION_RATE } from "./constants";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Одна сущность из вложенного select Supabase (в типах иногда массив). */
export function relationOne<T>(v: T | T[] | null | undefined): T | null {
  if (v == null) return null;
  return Array.isArray(v) ? (v[0] ?? null) : v;
}

export function formatPrice(amount: number): string {
  return `${amount.toLocaleString("ru-TJ")} ${CURRENCY_SYMBOL}`;
}

export function formatDate(date: string | Date): string {
  return format(new Date(date), "d MMM yyyy", { locale: ru });
}

/** Дата+время для подписи под сообщением в чате */
export function formatDateTimeChat(iso: string | Date): string {
  const d = new Date(iso);
  return format(d, "d MMM yyyy, HH:mm", { locale: ru });
}

const LAST_SEEN_ONLINE_SEC = 120;

/** Подпись «в сети / был в сети…» для чатов (ru). */
export function formatLastSeenRu(iso: string | null | undefined): string {
  if (!iso) return "Нет данных о посещении";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Нет данных о посещении";
  const sec = differenceInSeconds(new Date(), d);
  if (sec >= 0 && sec < LAST_SEEN_ONLINE_SEC) return "В сети";
  if (isToday(d)) return `Был в сети сегодня в ${format(d, "HH:mm")}`;
  if (isYesterday(d)) return `Был в сети вчера в ${format(d, "HH:mm")}`;
  return `Был в сети ${format(d, "d MMM yyyy, HH:mm", { locale: ru })}`;
}

export function formatDateRange(start: string, end: string): string {
  return `${formatDate(start)} — ${formatDate(end)}`;
}

/** rental + optional delivery + optional out-of-city surcharge; platform fee applies to the full amount */
export function calculateBookingPrice(
  pricePerDay: number,
  days: number,
  deliveryPrice: number = 0,
  outOfCitySurcharge: number = 0,
) {
  const rental = pricePerDay * days;
  const delivery = Math.max(0, Number(deliveryPrice) || 0);
  const outCity = Math.max(0, Number(outOfCitySurcharge) || 0);
  const total = rental + delivery + outCity;
  const fee = Math.round(total * COMMISSION_RATE);
  const payout = total - fee;
  return {
    total,
    fee,
    payout,
    rentalSubtotal: rental,
    deliverySubtotal: delivery,
    outOfCitySubtotal: outCity,
  };
}

export function daysBetween(start: Date, end: Date): number {
  const ms = end.getTime() - start.getTime();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

/** Контакт из notes заявки (см. формат в app/api/bookings/route.ts) — без вложенного select profiles под RLS */
export function parseBookingNotesContact(notes: string | null | undefined): { name?: string; phone?: string } {
  if (!notes) return {};
  const name = notes.match(/👤\s*(.+)/)?.[1]?.trim();
  const phone = notes.match(/📞\s*(.+)/)?.[1]?.trim();
  const out: { name?: string; phone?: string } = {};
  if (name) out.name = name;
  if (phone) out.phone = phone;
  return out;
}

/** Склонение числительных (1 день, 2 дня, 5 дней) */
export function pluralRu(n: number, one: string, few: string, many: string): string {
  const a = Math.abs(n) % 10;
  const b = Math.abs(n) % 100;
  if (b >= 11 && b <= 14) return many;
  if (a === 1) return one;
  if (a >= 2 && a <= 4) return few;
  return many;
}

/** Сколько календарных дней от сегодня до даты YYYY-MM-DD (положительно — в будущем) */
export function daysFromTodayToYmd(ymd: string): number {
  const [y, m, d] = ymd.split("-").map(Number);
  const end = new Date(y, m - 1, d);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((end.getTime() - today.getTime()) / (86_400_000));
}

/** Время из БД/API → HH:MM:SS */
export function parseReturnTimeHms(value: string | null | undefined): string | null {
  if (value == null || value === "") return null;
  const s = String(value).trim();
  const m = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return null;
  const hh = Math.min(23, parseInt(m[1], 10));
  const mm = Math.min(59, parseInt(m[2], 10));
  const ss = m[3] != null ? Math.min(59, parseInt(m[3], 10)) : 0;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

/**
 * Момент сдачи: end_date + return_time по Asia/Dushanbe (UTC+5).
 * Без return_time — до 23:59:59.999 последнего дня аренды.
 */
export function rentalReturnDeadlineUtc(ymd: string, returnTime?: string | null): Date {
  const [y, mo, d] = ymd.split("-").map(Number);
  const norm = parseReturnTimeHms(returnTime ?? null);
  let hours = 23;
  let minutes = 59;
  let seconds = 59;
  let ms = 999;
  if (norm) {
    const [hh, mm, ss] = norm.split(":").map((x) => parseInt(x, 10));
    hours = hh;
    minutes = mm;
    seconds = ss;
    ms = 0;
  }
  return new Date(Date.UTC(y, mo - 1, d, hours - 5, minutes, seconds, ms));
}

/** Срок сдачи для отображения (Asia/Dushanbe) */
export function formatBookingReturnDeadlineRu(ymd: string, returnTime?: string | null): string {
  return rentalReturnDeadlineUtc(ymd, returnTime).toLocaleString("ru-RU", {
    timeZone: "Asia/Dushanbe",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function pluralHours(n: number): string {
  return pluralRu(n, "час", "часа", "часов");
}

function futureReturnCountdown(ms: number): { label: string; tone: "ok" | "warn" } {
  const totalMins = Math.max(0, Math.floor(ms / 60_000));
  const days = Math.floor(totalMins / (24 * 60));
  const hours = Math.floor((totalMins % (24 * 60)) / 60);
  const mins = totalMins % 60;
  if (days >= 1) {
    let s = `Через ${days} ${pluralRu(days, "день", "дня", "дней")}`;
    if (hours > 0) s += ` ${hours} ${pluralHours(hours)}`;
    return { label: s, tone: "ok" };
  }
  if (hours >= 1) {
    let s = `Через ${hours} ${pluralHours(hours)}`;
    if (mins > 0) s += ` ${mins} мин.`;
    return { label: s, tone: hours < 6 ? "warn" : "ok" };
  }
  if (totalMins < 1 && ms > 0) return { label: "Меньше минуты", tone: "warn" };
  return { label: `Через ${Math.max(1, mins)} мин.`, tone: "warn" };
}

function overdueReturnCountdown(ms: number): { label: string; tone: "danger" } {
  const overdueMins = Math.max(1, Math.ceil(-ms / 60_000));
  const days = Math.floor(overdueMins / (24 * 60));
  const hours = Math.floor((overdueMins % (24 * 60)) / 60);
  const mins = overdueMins % 60;
  if (days >= 1) {
    let s = `Просрочка ${days} ${pluralRu(days, "день", "дня", "дней")}`;
    if (hours > 0) s += ` ${hours} ${pluralHours(hours)}`;
    return { label: s, tone: "danger" };
  }
  if (hours >= 1) {
    let s = `Просрочка ${hours} ${pluralHours(hours)}`;
    if (mins > 0) s += ` ${mins} мин.`;
    return { label: s, tone: "danger" };
  }
  return { label: `Просрочка ${mins} мин.`, tone: "danger" };
}

/**
 * Колонка «возврат»: обратный отсчёт до момента сдачи (return_time или 23:59 Душанбе в день end_date).
 */
export function vendorReturnHint(
  endDate: string,
  status: string,
  now: Date = new Date(),
  returnTime?: string | null,
): { label: string; tone: "neutral" | "ok" | "warn" | "danger" } {
  if (status === "cancelled") return { label: "—", tone: "neutral" };
  if (status === "completed") return { label: "Завершено", tone: "neutral" };
  const deadline = rentalReturnDeadlineUtc(endDate, returnTime);
  const ms = deadline.getTime() - now.getTime();
  if (ms > 0) return futureReturnCountdown(ms);
  if (ms === 0) {
    return { label: parseReturnTimeHms(returnTime) ? "Сейчас дедлайн сдачи" : "Дедлайн сдачи (23:59)", tone: "warn" };
  }
  return overdueReturnCountdown(ms);
}

/** До старта аренды (для подписи под периодом) */
export function rentalStartHint(startDate: string, status: string): string | null {
  if (status === "cancelled" || status === "completed") return null;
  const d = daysFromTodayToYmd(startDate);
  if (d > 0) return `Старт через ${d} ${pluralRu(d, "день", "дня", "дней")}`;
  if (d === 0) return "Старт сегодня";
  return null;
}
